<template>
  <v-layout>
    <!--
      Mounted with v-if on the breakpoint, NOT hidden with a CSS class.
      A `permanent` `app` drawer registers its layout offset with the shared
      Vuetify instance on mount -- hiding it with `hidden-sm-and-down`
      leaves that offset reserved (v-main padded for a drawer nobody can
      see) while looking identical in a screenshot. v-if mounts/unmounts it,
      which registers and unregisters the offset correctly. See
      docs/superpowers/specs/OPT-24-spec.md and OPT-24-log.md, "Codex
      review" C1.

      :mini-variant="!isNavExpanded" -- isNavExpanded/TOGGLE_NAV/
      handleNavToggle already existed (store/index.js, layout/mixins.js),
      wired through LayoutMixin, but their only prior consumer (the dead
      layout/index.vue) bound `:mini-variant="isNavExpanded"` -- backwards
      from what the name says, and never exercised live. Bound the way the
      name reads: isNavExpanded true (its default) means the rail starts
      expanded. See OPT-24-log.md, "Session 3" D14.
    -->
    <v-navigation-drawer
      v-if="$vuetify.breakpoint.mdAndUp"
      app
      permanent
      :mini-variant="!isNavExpanded"
      :mini-variant-width="72"
      color="white"
      class="s-rail no-print"
    >
      <!--
        The logo lives above the rail's own nav list, not in a top app-bar
        -- there is no app-bar any more (operator follow-up; see
        OPT-24-log.md, "Session 3"). Expanded shows the full wordmark
        (the OPT-225 recoloured hex lockup); collapsed shows
        src/assets/logo-mark.png, a direct crop of the hexagon icon out of
        the SAME logo.png -- not the unrelated src/assets/logo.svg Session
        3 used, which the operator correctly flagged as rendering the
        wrong mark (it is not a Sellary asset at all; see OPT-24-log.md,
        "Session 4"). Both PNGs are rendered from src/assets/brand/*.svg
        by scripts/brand/render-logos.sh.
      -->
      <div class="s-rail-logo" :class="{ 's-rail-logo--collapsed': !isNavExpanded }">
        <v-img
          v-if="isNavExpanded"
          src="@/assets/logo.png"
          height="36"
          width="140"
          contain
          class="s-rail-logo__mark s-rail-logo__mark--wordmark"
        />
        <v-img
          v-else
          src="@/assets/logo-mark.png"
          height="28"
          width="28"
          contain
          class="s-rail-logo__mark s-rail-logo__mark--icon"
        />
        <v-btn
          icon
          small
          class="s-rail-logo__toggle"
          :aria-label="isNavExpanded ? 'Collapse navigation' : 'Expand navigation'"
          @click="handleNavToggle"
        >
          <v-icon small>{{ isNavExpanded ? "mdi-chevron-left" : "mdi-chevron-right" }}</v-icon>
        </v-btn>
      </div>
      <v-divider></v-divider>

      <v-list nav dense>
        <template v-for="(item, index) in navElements">
          <!--
            Regular single-level routes. Per-route icon colors
            (getRouteColor) are deliberately dropped in the rail -- the
            active/inactive treatment below is what the brief means by
            "the anchor is color and treatment" (see OPT-24-log.md, D8).
          -->
          <v-list-item
            :key="'rail-' + index"
            :to="item.path"
            class="s-rail-item"
            active-class="s-rail-item--active"
          >
            <v-list-item-icon>
              <v-badge :value="routeBadge(item) > 0" :content="routeBadge(item)" color="error" overlap>
                <v-icon :size="isNavExpanded ? 18 : undefined">{{ getRouteIcon(item) }}</v-icon>
              </v-badge>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ getRouteTitle(item) }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>

      <template v-slot:append>
        <v-divider></v-divider>
        <v-list nav dense>
          <!--
            navElements (above) does not include Logout -- it is a separate
            v-list-item in the mobile dialog nav below, outside that
            computed. Rendering only navElements in the rail while the
            hamburger is hidden at md+ would leave desktop users with no
            way to sign out at all. See OPT-24-log.md, "Codex review" C15.
          -->
          <v-list-item class="s-rail-item" @click="logout">
            <v-list-item-icon>
              <v-icon :size="isNavExpanded ? 18 : undefined">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!--
      No app-bar any more -- removed per the operator's "remove the top
      sellary bar at top across the content areas" (Session 3). Mobile
      still needs SOME trigger for the fullscreen nav dialog below (the
      brief's "the hamburger stays"), so it becomes a floating button too,
      for the same reason notifications does, positioned not to collide
      with it (menu top-left, notifications top-right). See D15.
    -->
    <v-btn
      v-if="!$vuetify.breakpoint.mdAndUp"
      fab
      small
      color="white"
      class="s-floating-btn s-floating-btn--menu no-print"
      aria-label="Open navigation"
      @click="dialog = true"
    >
      <v-icon>mdi-menu</v-icon>
    </v-btn>

    <!--
      NotificationBell's own activator is a v-btn -- wrapping it in ANOTHER
      v-btn would nest interactive elements, so this uses v-sheet's own
      `rounded="circle"` + elevation for the floating-button look instead.
      NotificationBell.vue itself is unchanged.
    -->
    <v-sheet
      v-if="!flushTop"
      rounded="circle"
      elevation="2"
      class="s-floating-btn s-floating-btn--notifications no-print"
    >
      <NotificationBell />
    </v-sheet>

    <v-main style="width:100%" :class="{ 's-main--flush': flushTop, 's-main--owns-top': ownsTop }">
      <v-container fluid class="pa-0 ma-0">
        <ConnectFirstPrompt />
        <!-- A route with meta.feature shows its view only on a plan that has the feature. -->
        <plan-gate v-if="gateFeature" :key="gateFeature" :feature="gateFeature" :title="gateTitle">
          <router-view />
        </plan-gate>
        <router-view v-else />
      </v-container>
    </v-main>
    <v-sheet class="flex no-print">
      <v-dialog
        v-if="!$vuetify.breakpoint.mdAndUp"
        v-model="dialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <v-toolbar-side-icon></v-toolbar-side-icon>
        <v-card dark>
          <v-toolbar color="secondary" dark>
            <v-spacer></v-spacer>
            <v-toolbar-title> Site Navigation</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-list>
            <template v-for="(item, index) in mobileNavElements">
              <!-- Regular single-level routes -->
              <v-list-item
                :key="index"
                :to="item.path"
                @click.native="dialog = false"
              >
                <v-list-item-icon>
                  <v-badge :value="routeBadge(item) > 0" :content="routeBadge(item)" color="error" overlap>
                    <v-icon :color="getRouteColor(item)">{{ getRouteIcon(item) }}</v-icon>
                  </v-badge>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ getRouteTitle(item) }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <v-divider></v-divider>
            <v-list-item @click="logout"> Logout </v-list-item>
          </v-list>
        </v-card>
      </v-dialog>
    </v-sheet>
  </v-layout>
</template>
<script>
import { LayoutMixin } from "@/layout/mixins";
import { Auth } from "aws-amplify";
import { claimStoredReferral } from "@/utils/referral";
import { createAccountsRefresher } from "@/utils/accountsRefresher";

const MESSAGES_POLL_MS = 120000;

export default {
  mixins: [LayoutMixin],
  name: "Dashboard",
  components: {
    PlanGate: () => import("@/components/billing/PlanGate.vue"),
    NotificationBell: () => import("@/components/NotificationBell"),
    ConnectFirstPrompt: () => import("@/components/onboarding/ConnectFirstPrompt"),
  },
  props: {
    source: String,
  },
  data: () => ({
    dialog: false,
  }),
  computed: {
    // OPT-299: Settings/Admin (meta.flushTop) start at the window's top edge on
    // desktop; SectionShell hosts the bell in its column there.
    flushTop() {
      return (
        this.$vuetify.breakpoint.mdAndUp && this.$route.matched.some((r) => r.meta && r.meta.flushTop)
      );
    },
    // Pages that draw their own header at the top (meta.ownsTop) drop the 80px
    // reserve on every breakpoint; the floating bell and phone menu stay.
    ownsTop() {
      return this.$route.matched.some((r) => r.meta && r.meta.ownsTop);
    },
    // The matched route's meta.feature (Orders, Messages, Profit): the plan feature that opens the view.
    gateRoute() {
      const m = this.$route.matched.filter((r) => r.meta && r.meta.feature);
      return m.length ? m[m.length - 1] : null;
    },
    gateFeature() { return this.gateRoute ? this.gateRoute.meta.feature : ""; },
    gateTitle() { return this.gateRoute ? this.gateRoute.meta.title : ""; },
    navElements() {
      return this.$router.options.routes.filter((item) => {
        // Skip hidden routes
        if (item.hidden) return false;

        // Check admin requirement at the appropriate level
        let requiresAdmin = false;

        if (item.meta && item.meta.requiresAdmin !== undefined) {
          // Parent-level meta (like Settings)
          requiresAdmin = item.meta.requiresAdmin;
        } else if (item.children && item.children[0] && item.children[0].meta) {
          // Child-level meta (like most other routes)
          requiresAdmin = item.children[0].meta.requiresAdmin;
        }

        // OPT-203: operator routes need Admins; tenant routes need Subscribers or Admins.
        return requiresAdmin ? this.$store.getters.isAdmin : this.$store.getters.hasAppAccess;
      });
    },
    // The mobile drawer never lists desktop-only pages (the Cancellations
    // queue is deliberately kept off phones -- see the 2026-09-26 spec, s5).
    mobileNavElements() {
      return this.navElements.filter((item) => {
        const meta = item.meta || (item.children && item.children[0] && item.children[0].meta) || {};
        return !meta.desktopOnly;
      });
    },
  },
  mounted() {
    // A user with no group sees only the delete-account page: no polling (OPT-203).
    if (!this.$store.getters.hasAppAccess) return;
    // Orders and messages are tenant pages (OPT-204): Subscribers and Admins.
    if (this.$vuetify.breakpoint.mdAndUp) {
      Promise.resolve(this.$store.dispatch("refreshCancellations")).catch(() => {});
    }
    // Unread buyer messages: on mount, every 2 minutes, and when the tab
    // regains focus. Not gated on breakpoint, unlike cancellations: staff
    // answer buyers from their phones. The 15-minute pull is the real latency,
    // so a poll beats a subscription here (OPT-67 D8).
    this.refreshMessages();
    // OPT-310: the account list is read on every load and on focus, not only at sign-in.
    this.accountsRefresher = createAccountsRefresher((a) => this.$store.dispatch(a));
    this.accountsRefresher.refresh(true);
    window.addEventListener("focus", this.refreshAccountsOnFocus);
    // OPT-246: a creator link clicked before sign-in is claimed on the first signed-in load.
    claimStoredReferral();
    this.messagesTimer = setInterval(this.refreshMessages, MESSAGES_POLL_MS);
    window.addEventListener("focus", this.refreshMessages);
  },
  beforeDestroy() {
    clearInterval(this.messagesTimer);
    window.removeEventListener("focus", this.refreshMessages);
    window.removeEventListener("focus", this.refreshAccountsOnFocus);
    if (this.accountsRefresher) this.accountsRefresher.stop();
  },
  created() {},
  methods: {
    refreshAccountsOnFocus() {
      if (this.accountsRefresher) this.accountsRefresher.refresh();
    },
    refreshMessages() {
      return Promise.resolve(this.$store.dispatch("refreshMessagesUnread")).catch(() => {});
    },
    routeBadge(item) {
      const meta = item.meta || (item.children && item.children[0] && item.children[0].meta) || {};
      return meta.badge ? this.$store.getters[meta.badge] || 0 : 0;
    },
    getRouteTitle(item) {
      // Get title from parent meta or first child meta
      if (item.meta && item.meta.title) {
        return item.meta.title;
      } else if (item.children && item.children[0] && item.children[0].meta) {
        return item.children[0].meta.title;
      }
      return item.name || 'Unknown';
    },
    getRouteIcon(item) {
      // Get icon from parent meta or first child meta
      if (item.meta && item.meta.icon) {
        return item.meta.icon;
      } else if (item.children && item.children[0] && item.children[0].meta) {
        return item.children[0].meta.icon;
      }
      return 'mdi-help-circle';
    },
    getRouteColor(item) {
      // Get color from parent meta or first child meta
      if (item.meta && item.meta.color) {
        return item.meta.color;
      } else if (item.children && item.children[0] && item.children[0].meta) {
        return item.children[0].meta.color;
      }
      return 'grey';
    },
    getUser() {
      let email = "Not Logged In";
      if (this.$store.state.user != null) email = this.$store.state.user.email;

      return email;
    },
    async logout() {
      this.$store.dispatch("notifications/unsubscribe");
      this.$store.commit("RESET");
      try {
        await Auth.signOut();
      } catch (error) {
        console.log("error");
      }
      this.$router.push({ name: "Login" });
    },
  },
};
</script>
<style scoped>
.s-rail {
  border-right: 1px solid var(--s-color-border);
}

.s-rail-logo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  min-height: 64px;
}

.s-rail-logo--collapsed {
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.s-rail-item {
  border-radius: var(--s-radius-control, 10px);
  color: var(--s-color-ink);
}

.s-rail-item >>> .v-icon {
  color: var(--s-color-muted);
}

.s-rail:not(.v-navigation-drawer--mini-variant) .s-rail-item >>> .v-list-item__icon:first-child {
  margin-right: 12px;
  min-width: 18px;
}

.s-rail-item.s-rail-item--active {
  background-color: var(--s-color-primary);
  color: #ffffff;
}

.s-rail-item.s-rail-item--active >>> .v-icon {
  color: #ffffff;
}

/*
  OPT-34: centre the icon in the collapsed rail.

  Two Vuetify rules fight over this icon's right margin, and BOTH are
  (0,3,0) -- two classes plus one pseudo-class, with `>` and `*` adding
  nothing:

    .v-navigation-drawer--mini-variant .v-list-item > *:first-child
        { margin-left: 0; margin-right: 0 }        <- mini-variant's own reset
    .v-application--is-ltr .v-list-item__icon:first-child
        { margin-right: 32px }                     <- VListItem's icon/label gap

  Neither is !important, so a tie is broken by source order -- and because
  the app builds Vuetify a-la-carte (src/plugins/vuetify.js imports
  vuetify/lib, vuetify-loader pulls each component's own .sass), the emitted
  order follows webpack's module graph. That order is REVERSED between the
  two ways Vuetify reaches a page:

    dev server (injected styles):  reset last  -> margin-right: 0  -> centred
    production (extracted CSS):    gap last    -> margin-right: 32 -> 16px left

  So this bug does not reproduce on `npm run serve`. It is only visible in a
  production build, which is why it survived review. Verify this rule with
  `npm run build`, never with the dev server.

  The 16px is the signature: with the 32px margin surviving, free space in
  the row's 39px content box is 39 - 56 = -17px, and `justify-content:
  center` (applied because mini-variant-width 72 != 56) splits negative
  space both ways -- displacing the icon by half the surviving margin,
  independent of row width.

  This override therefore has to win on SPECIFICITY, not order. Scoped, it
  compiles to
    .s-rail.v-navigation-drawer--mini-variant[data-v-x] .v-list-item__icon:first-child
  = (0,5,0), which beats (0,3,0) whichever way the module graph emits them.
  Relying on source order is what broke this in the first place.

  Collapsed rail only: the expanded rail keeps Vuetify's icon/label gap
  (OPT-34-spec.md, "Not in this").
*/
.s-rail.v-navigation-drawer--mini-variant >>> .v-list-item__icon:first-child {
  margin-left: 0;
  margin-right: 0;
}

/*
  Floating buttons replace the app-bar for global actions (notifications
  everywhere, the mobile menu trigger). Positioned top corners rather than
  the conventional bottom-right FAB spot -- notifications lived top-right
  in the old app-bar, and keeping it there is the smaller visual jump.
*/
.s-floating-btn {
  position: fixed;
  top: 16px;
  z-index: 200;
}

.s-floating-btn--menu {
  left: 16px;
}

.s-floating-btn--notifications {
  right: 16px;
}

/*
  With no app-bar, Vuetify's applicationable mixin computes v-main's
  padding-top as 0 -- nothing registers a height any more. Routed content
  then starts at the very top of the viewport, directly under the fixed
  floating buttons: on Accounts the notifications button sits on top of
  the "Add account" button and intercepts its clicks, and on mobile the
  menu button covers the page heading -- on every page, not just this
  one, since this padding is shell-wide. `!important` is required because
  Vuetify sets padding-top as an inline style, which otherwise outranks a
  plain class rule regardless of source order. Found in codex review; see
  OPT-24-log.md, "Session 3".
*/
.v-main {
  padding-top: 80px !important;
}

/* OPT-299: flush pages (Settings, Admin) opt out; two classes beat one. */
.v-main.s-main--flush {
  padding-top: 0 !important;
}

/* Pages with their own top header (meta.ownsTop) drop the reserve on every breakpoint. */
.v-main.s-main--owns-top {
  padding-top: 0 !important;
}

/*
  Printing at desktop width leaves $vuetify.breakpoint.mdAndUp true --
  print media doesn't change window.innerWidth -- so the rail stays
  MOUNTED. Resetting v-main's padding is necessary (that padding is a
  reactive inline style Vuetify's applicationable mixin computed while the
  drawer was mounted, not CSS-driven, so hiding the drawer alone leaves it
  behind), but resetting it WITHOUT also hiding the drawer here would move
  printed content underneath its still-visible fixed positioning. index2.vue
  is a static top-level import (router/index.js:3), so hiding it here
  guarantees this on every page regardless of navigation history -- unlike
  the pre-existing `.no-print` rule (dropoff.vue), whose CSS chunk is only
  fetched once a browser session visits a lazy-loaded route that carries it.
  Found in codex review; see OPT-24-log.md.
*/
@media print {
  .v-navigation-drawer,
  .s-floating-btn {
    display: none !important;
  }
  .v-main {
    padding-left: 0 !important;
    padding-top: 0 !important;
  }
}
</style>
